import { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useSearch } from "wouter";
import { useAppStore } from "@/store";
import {
  useGenerateAllPlatforms,
  useRefinePost,
  useCreateIdea,
  useGetVoiceProfile,
  useSubmitVoiceFeedback,
  GeneratePostRequestPlatform,
  GeneratePostRequestPostLengthOverride,
  GeneratePostRequestPostStyle,
  GeneratePostRequestSpacingStyle,
  GeneratePostRequestHookStyle,
  GeneratePostRequestPerspective,
  GeneratePostRequestHumorLevel,
  GeneratePostRequestAudience,
  GeneratePostRequestEmojiUsage,
} from "@workspace/api-client-react";
import { apiFetch } from "@/lib/api-fetch";
import { getWordCount } from "@/lib/post-utils";
import { copyForPlatform } from "@/lib/copy-for-platform";
import { useToast } from "@/hooks/use-toast";
import { PaywallModal, usePaywallCheck } from "@/components/paywall-modal";
import { useInlineVoiceInput } from "@/hooks/use-inline-voice-input";
import { useIntentDetection } from "@/hooks/use-intent-detection";
import { useVaguenessDetection } from "@/hooks/use-vagueness-detection";
import {
  PLATFORM_CONFIG,
  PLATFORM_LABELS,
  PLATFORM_SETTING_DESCRIPTIONS,
  STYLE_SUGGESTIONS,
  ConflictWarning,
  extractErrorMessage,
} from "@/lib/post-generator-config";

export type DraftItem = { content: string; score: number; id: string; styleNote?: string };

export function usePostGenerator() {
  const { currentIdea, setIdea, sessionId, selectedPlatform, setPlatform, incrementPostsGenerated } = useAppStore();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const searchString = useSearch();

  const [tone, setTone] = useState([50]);
  const [copied, setCopied] = useState<string | null>(null);
  const [savedToVault, setSavedToVault] = useState(false);
  const [mode, setMode] = useState<"single" | "all">("single");
  const [allPlatformResults, setAllPlatformResults] = useState<Record<string, string> | null>(null);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [generateAllError, setGenerateAllError] = useState<string | null>(null);
  const [postLengthOverride, setPostLengthOverride] = useState<GeneratePostRequestPostLengthOverride | undefined>(undefined);
  const [feedbackGiven, setFeedbackGiven] = useState<"up" | "down" | null>(null);
  const [feedbackMessage, setFeedbackMessage] = useState<string | null>(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [postStyle, setPostStyle] = useState<GeneratePostRequestPostStyle | undefined>(undefined);
  const [spacingStyle, setSpacingStyle] = useState<GeneratePostRequestSpacingStyle | undefined>(undefined);
  const [hookStyle, setHookStyle] = useState<GeneratePostRequestHookStyle | undefined>(undefined);
  const [perspective, setPerspective] = useState<GeneratePostRequestPerspective | undefined>(undefined);
  const [humorLevel, setHumorLevel] = useState<GeneratePostRequestHumorLevel | undefined>(undefined);
  const [audience, setAudience] = useState<GeneratePostRequestAudience | undefined>(undefined);
  const [emojiUsage, setEmojiUsage] = useState<GeneratePostRequestEmojiUsage | undefined>(undefined);
  const [targetWordCount, setTargetWordCount] = useState<string>("");
  const [customInstruction, setCustomInstruction] = useState<string>("");
  const [selectedVoiceState, setSelectedVoiceState] = useState<string | undefined>(undefined);
  const [audienceDescription, setAudienceDescription] = useState<string>("");
  const [blendStyle, setBlendStyle] = useState<string | undefined>(undefined);
  const [recentTopics, setRecentTopics] = useState<Array<{ topic: string; format: string }>>([]);
  const [draftContent, setDraftContent] = useState<string>("");
  const [editedAllResults, setEditedAllResults] = useState<Record<string, string>>({});
  const [isTrimming, setIsTrimming] = useState(false);
  const [voiceIntentBanner, setVoiceIntentBanner] = useState<string | null>(null);
  const [platform, setLocalPlatform] = useState<GeneratePostRequestPlatform>(
    selectedPlatform as GeneratePostRequestPlatform
  );
  const [drafts, setDrafts] = useState<DraftItem[] | null>(null);
  const [activeDraftIdx, setActiveDraftIdx] = useState(0);
  const [isBatchLoading, setIsBatchLoading] = useState(false);
  const [undoStack, setUndoStack] = useState<string[]>([]);
  const [multiVariant, setMultiVariant] = useState(false);
  const [result, setResult] = useState<{ content: string; score: number; id: string } | null>(null);
  const [coachTip, setCoachTip] = useState<string | null>(null);
  const [coachLoading, setCoachLoading] = useState(false);
  const [coachDismissed, setCoachDismissed] = useState(false);
  const [voiceDnaOpen, setVoiceDnaOpen] = useState(false);
  const [showHardPaywall, setShowHardPaywall] = useState(false);
  const [autoGenerating, setAutoGenerating] = useState(false);
  const [voiceNudgeDismissed, setVoiceNudgeDismissed] = useState(false);
  const [trimmingPlatform, setTrimmingPlatform] = useState<string | null>(null);

  const autoGenerateFiredRef = useRef(false);

  const baseUrl = import.meta.env.BASE_URL.replace(/\/$/, "");

  const { showPaywall, checkPaywall, forceShowPaywall, dismissPaywall } = usePaywallCheck();

  const { data: voiceProfile } = useGetVoiceProfile({
    request: { headers: { "x-session-id": sessionId } },
  });
  const voiceSampleCount = voiceProfile?.sampleCount ?? 0;
  const needsVoiceTraining = voiceSampleCount < 3;
  const hasPreferences = !!(voiceProfile?.preferences && Object.keys(voiceProfile.preferences as object).length > 0);
  const voiceStates = (voiceProfile?.analysis as Record<string, unknown> | undefined)?.voiceStates as Array<{ label: string; description: string; dominantTone: string; exampleContext: string }> | undefined;

  useEffect(() => {
    const params = new URLSearchParams(searchString);
    const urlPlatform = params.get("platform");
    if (urlPlatform && Object.values(GeneratePostRequestPlatform).includes(urlPlatform as GeneratePostRequestPlatform)) {
      setLocalPlatform(urlPlatform as GeneratePostRequestPlatform);
    }
  }, [searchString]);

  useEffect(() => {
    setPlatform(platform as "twitter" | "linkedin" | "reddit" | "instagram");
  }, [platform, setPlatform]);

  useEffect(() => {
    if (result?.content !== undefined) setDraftContent(result.content);
  }, [result?.content]);

  useEffect(() => {
    if (allPlatformResults) setEditedAllResults({ ...allPlatformResults });
  }, [allPlatformResults]);

  const advancedCount = [postStyle, spacingStyle, hookStyle, perspective, humorLevel, audience, emojiUsage, selectedVoiceState, blendStyle].filter(Boolean).length + (customInstruction.trim() ? 1 : 0) + (targetWordCount.trim() ? 1 : 0) + (audienceDescription.trim() ? 1 : 0);

  const clearAdvanced = () => {
    setPostStyle(undefined); setSpacingStyle(undefined); setHookStyle(undefined); setPerspective(undefined);
    setHumorLevel(undefined); setAudience(undefined); setEmojiUsage(undefined);
    setTargetWordCount(""); setCustomInstruction(""); setSelectedVoiceState(undefined);
    setAudienceDescription(""); setBlendStyle(undefined);
  };

  const platformDesc = PLATFORM_SETTING_DESCRIPTIONS[platform] ?? {};
  const currentSuggestions = postStyle ? (STYLE_SUGGESTIONS[postStyle] ?? {}) : {};

  const conflicts: ConflictWarning[] = [];
  if (audience === "executive" && humorLevel === "playful") {
    conflicts.push({ fields: ["audience", "humor"], message: "Executive audience + Playful humor can feel mismatched — C-suite readers expect composed, measured communication." });
  }
  if (postStyle === "listicle" && spacingStyle === "tight") {
    conflicts.push({ fields: ["postStyle", "spacing"], message: "Listicle style works best with Airy spacing — tight paragraphs obscure list readability." });
  }
  if (postStyle === "personal" && perspective === "second-person") {
    conflicts.push({ fields: ["postStyle", "perspective"], message: "Personal (introspective) style paired with 'You' perspective can feel disconnected — 'I / Me' tends to land better for personal stories." });
  }

  const transcribeAudioInline = useCallback(
    async (audioBase64: string) => {
      const res = await apiFetch(`${baseUrl}/api/voice/transcribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-session-id": sessionId },
        body: JSON.stringify({ audioBase64, sessionId }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({})) as { error?: string };
        throw new Error(body.error ?? "Transcription failed");
      }
      return res.json() as Promise<{ text: string }>;
    },
    [baseUrl, sessionId]
  );

  const { detectIntent } = useIntentDetection({ sessionId, baseUrl });

  const handleVoiceTranscriptForTopic = useCallback(
    async (text: string) => {
      setIdea(text);
      const intent = await detectIntent(text);
      if (!intent) return;
      const displayText = intent.displayText || text;
      setIdea(displayText);
      if (intent.suggestedAction === "fill_and_generate") {
        setVoiceIntentBanner(`Generating your post about "${displayText.slice(0, 50)}${displayText.length > 50 ? "…" : ""}"…`);
        setTimeout(() => setVoiceIntentBanner(null), 5000);
      }
    },
    [setIdea, detectIntent]
  );

  const topicVoiceInput = useInlineVoiceInput({
    onTranscript: handleVoiceTranscriptForTopic,
    transcribeAudio: transcribeAudioInline,
  });

  const isIdeaVague = useVaguenessDetection(currentIdea);

  const handleSubscriptionError = (err: Error) => {
    const msg = extractErrorMessage(err, "");
    if (/free limit|subscribe|subscription required/i.test(msg)) {
      setShowHardPaywall(true);
      return true;
    }
    return false;
  };

  const generateAllMutation = useGenerateAllPlatforms({
    mutation: {
      onSuccess: (data) => {
        setAllPlatformResults(data.platforms);
        setGenerateAllError(null);
        setSavedToVault(false);
        incrementPostsGenerated();
        setCustomInstruction("");
      },
      onError: (err: Error) => {
        if (handleSubscriptionError(err)) return;
        const message = extractErrorMessage(err, "Post generation failed. Please try again.");
        setGenerateAllError(message);
        toast({ title: "Generation failed", description: message, variant: "destructive" });
      }
    },
    request: { headers: { "x-session-id": sessionId } },
  });

  const refineMutation = useRefinePost({
    mutation: {
      onSuccess: (data) => {
        setResult({ content: data.post.content, score: data.voiceMatchScore, id: data.post.id });
        setGenerateError(null);
      },
      onError: (err: Error) => {
        setUndoStack(prev => prev.slice(1));
        const message = extractErrorMessage(err, "Could not refine post. Please try again.");
        toast({ title: "Refinement failed", description: message, variant: "destructive" });
      }
    },
    request: { headers: { "x-session-id": sessionId } },
  });

  const createIdeaMutation = useCreateIdea({
    mutation: {
      onSuccess: () => {
        setSavedToVault(true);
        toast({ title: "Saved to Vault!" });
      },
      onError: () => {
        toast({ title: "Failed to save", variant: "destructive" });
      }
    },
    request: { headers: { "x-session-id": sessionId } },
  });

  const feedbackMutation = useSubmitVoiceFeedback({
    mutation: {
      onSuccess: (data) => { setFeedbackMessage(data.message); },
      onError: () => { setFeedbackMessage("Feedback recorded"); },
    },
    request: { headers: { "x-session-id": sessionId } },
  });

  const fetchCoachTip = useCallback(async (content: string, currentPlatform: string) => {
    setCoachTip(null);
    setCoachDismissed(false);
    setCoachLoading(true);
    try {
      const res = await apiFetch(`${baseUrl}/api/posts/coach`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-session-id": sessionId },
        body: JSON.stringify({ content, platform: currentPlatform }),
      });
      if (res.ok) {
        const data = await res.json() as { tip: string | null };
        setCoachTip(data.tip ?? null);
      }
    } catch {
    } finally {
      setCoachLoading(false);
    }
  }, [sessionId, baseUrl]);

  const updateActiveDraftContent = useCallback((content: string) => {
    setDrafts((prev) => {
      if (!prev) return prev;
      const next = [...prev];
      next[activeDraftIdx] = { ...next[activeDraftIdx]!, content };
      return next;
    });
  }, [activeDraftIdx]);

  const commitActiveDraft = useCallback(() => {
    if (!drafts) return;
    const chosen = drafts[activeDraftIdx];
    if (!chosen) return;
    setResult({ content: chosen.content, score: chosen.score, id: chosen.id });
    setDraftContent(chosen.content);
    setDrafts(null);
    setUndoStack([]);
    setSavedToVault(false);
    setFeedbackGiven(null);
    setFeedbackMessage(null);
    fetchCoachTip(chosen.content, platform);
  }, [drafts, activeDraftIdx, fetchCoachTip, platform]);

  const handleGenerate = useCallback(async () => {
    if (!currentIdea.trim()) return;
    if (checkPaywall()) return;
    const wordCount = currentIdea.trim().split(/\s+/).filter(Boolean).length;
    if (wordCount < 3) {
      const msg = "Your idea needs a bit more context — describe what happened, what you learned, or what you want to say (at least a few words).";
      setGenerateError(msg);
      setGenerateAllError(msg);
      return;
    }
    setGenerateError(null);
    setGenerateAllError(null);
    setFeedbackGiven(null);
    setFeedbackMessage(null);
    setCoachTip(null);
    setCoachLoading(false);
    setCoachDismissed(false);
    const parsedWordCount = parseInt(targetWordCount, 10);
    const wordCountParam = !isNaN(parsedWordCount) && parsedWordCount >= 5 ? parsedWordCount : undefined;
    const advancedParams = { postStyle, spacingStyle, hookStyle, perspective, humorLevel, audience, emojiUsage, targetWordCount: wordCountParam };
    const safeAudienceDescription = audienceDescription.trim() || undefined;
    const safeBlendStyle = blendStyle || undefined;
    if (mode === "all") {
      setAllPlatformResults(null);
      generateAllMutation.mutate({
        data: { idea: currentIdea, tone: tone[0], sessionId, postLengthOverride, ...advancedParams, customInstruction: customInstruction.trim() || undefined }
      });
    } else if (multiVariant) {
      setResult(null);
      setDrafts(null);
      setUndoStack([]);
      setIsBatchLoading(true);
      try {
        const resp = await apiFetch(`${baseUrl}/api/posts/generate-batch`, {
          method: "POST",
          headers: { "Content-Type": "application/json", "x-session-id": sessionId },
          body: JSON.stringify({ idea: currentIdea, platform, tone: tone[0], postLengthOverride, ...advancedParams, customInstruction: customInstruction.trim() || undefined, voiceState: selectedVoiceState, audienceDescription: safeAudienceDescription, blendStyle: safeBlendStyle }),
        });
        if (resp.status === 403) {
          const errBody = await resp.json().catch(() => ({})) as { code?: string };
          if (errBody.code === "FREE_LIMIT_REACHED" || errBody.code === "SUBSCRIPTION_REQUIRED") { setShowHardPaywall(true); return; }
        }
        if (!resp.ok) {
          const errBody = await resp.json().catch(() => ({})) as { error?: string };
          throw new Error(errBody.error || "Post generation failed. Please try again.");
        }
        const data = await resp.json() as { drafts: Array<{ content: string; voiceMatchScore: number; voiceMatchRationale: string; postId: string; styleNote?: string }> };
        setDrafts(data.drafts.map((d) => ({ content: d.content, score: d.voiceMatchScore, id: d.postId, styleNote: d.styleNote })));
        setActiveDraftIdx(0);
        setSavedToVault(false);
        incrementPostsGenerated();
      } catch (err) {
        const message = err instanceof Error ? err.message : "Post generation failed. Please try again.";
        setGenerateError(message);
        toast({ title: "Generation failed", description: message, variant: "destructive" });
      } finally {
        setIsBatchLoading(false);
      }
    } else {
      setResult(null);
      setDrafts(null);
      setUndoStack([]);
      setIsBatchLoading(true);
      try {
        const resp = await apiFetch(`${baseUrl}/api/posts/generate`, {
          method: "POST",
          headers: { "Content-Type": "application/json", "x-session-id": sessionId },
          body: JSON.stringify({ idea: currentIdea, platform, tone: tone[0], postLengthOverride, ...advancedParams, customInstruction: customInstruction.trim() || undefined, voiceState: selectedVoiceState, audienceDescription: safeAudienceDescription, blendStyle: safeBlendStyle }),
        });
        if (resp.status === 403) {
          const errBody = await resp.json().catch(() => ({})) as { code?: string };
          if (errBody.code === "FREE_LIMIT_REACHED" || errBody.code === "SUBSCRIPTION_REQUIRED") { setShowHardPaywall(true); return; }
        }
        if (!resp.ok) {
          const errBody = await resp.json().catch(() => ({})) as { error?: string };
          throw new Error(errBody.error || "Post generation failed. Please try again.");
        }
        const data = await resp.json() as { post: { id: string; content: string }; voiceMatchScore: number };
        setResult({ content: data.post.content, score: data.voiceMatchScore, id: data.post.id });
        setDraftContent(data.post.content);
        setSavedToVault(false);
        incrementPostsGenerated();
        fetchCoachTip(data.post.content, platform);
      } catch (err) {
        const message = err instanceof Error ? err.message : "Post generation failed. Please try again.";
        setGenerateError(message);
        toast({ title: "Generation failed", description: message, variant: "destructive" });
      } finally {
        setIsBatchLoading(false);
      }
    }
  }, [currentIdea, checkPaywall, mode, multiVariant, tone, sessionId, platform, postLengthOverride, postStyle, spacingStyle, hookStyle, perspective, humorLevel, audience, emojiUsage, targetWordCount, customInstruction, selectedVoiceState, audienceDescription, blendStyle, generateAllMutation, toast, incrementPostsGenerated, fetchCoachTip, baseUrl]);

  useEffect(() => {
    if (autoGenerateFiredRef.current) return;
    const params = new URLSearchParams(searchString);
    if (params.get("autoGenerate") === "1" && currentIdea.trim()) {
      autoGenerateFiredRef.current = true;
      setAutoGenerating(true);
      const t = setTimeout(() => {
        setAutoGenerating(false);
        handleGenerate();
      }, 150);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [searchString, currentIdea, handleGenerate]);

  const fetchRecentContext = useCallback(async () => {
    try {
      const res = await apiFetch(`${baseUrl}/api/studio/context`, {
        headers: { "x-session-id": sessionId },
      });
      if (res.ok) {
        const data = await res.json() as { recentTopics: Array<{ topic: string; format: string }> };
        setRecentTopics(data.recentTopics ?? []);
      }
    } catch { /* non-critical */ }
  }, [sessionId, baseUrl]);

  const clearRecentContext = useCallback(async () => {
    try {
      await apiFetch(`${baseUrl}/api/studio/context`, {
        method: "DELETE",
        headers: { "x-session-id": sessionId },
      });
      setRecentTopics([]);
    } catch { /* ignore */ }
  }, [sessionId, baseUrl]);

  useEffect(() => {
    if (advancedOpen) fetchRecentContext();
  }, [advancedOpen, fetchRecentContext]);

  const handleRefine = (action: "shorten" | "expand" | "polish" | "regenerate") => {
    if (!result?.id) return;
    setUndoStack(prev => [draftContent, ...prev].slice(0, 5));
    refineMutation.mutate({ postId: result.id, data: { action, toneValue: tone[0], sessionId } });
  };

  const handleUndo = () => {
    if (!undoStack.length) return;
    setDraftContent(undoStack[0]!);
    setUndoStack(prev => prev.slice(1));
  };

  const handleCopy = (text: string, id: string = "main", plat?: string) => {
    const cleanText = copyForPlatform(text, plat ?? platform);
    navigator.clipboard.writeText(cleanText);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
    const label = PLATFORM_LABELS[plat ?? platform] ?? "clipboard";
    toast({ title: `Copied for ${label}!` });
  };

  const handleSaveToVault = () => {
    const ALL_PLATFORMS = ["twitter", "linkedin", "reddit", "instagram", "facebook"] as const;
    if (allPlatformResults) {
      const combinedNotes = ALL_PLATFORMS
        .map((p) => {
          const content = editedAllResults[p] ?? allPlatformResults[p] ?? "";
          return content ? `## ${PLATFORM_LABELS[p] ?? p}\n\n${content}` : null;
        })
        .filter(Boolean)
        .join("\n\n---\n\n");
      createIdeaMutation.mutate({
        data: { title: currentIdea.slice(0, 100) || "Generated posts", notes: combinedNotes || "Generated posts", tags: ALL_PLATFORMS.filter((p) => allPlatformResults[p]), source: "generator" },
      });
    } else {
      if (!draftContent) return;
      createIdeaMutation.mutate({
        data: { title: currentIdea.slice(0, 100) || "Generated post", notes: draftContent, tags: [platform], platform, source: "generator" },
      });
    }
  };

  const handleTrimToLimit = async () => {
    if (!draftContent || isTrimming) return;
    setIsTrimming(true);
    try {
      const resp = await fetch(`${baseUrl}/api/posts/trim`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-session-id": sessionId },
        body: JSON.stringify({ content: draftContent, platform }),
      });
      if (resp.ok) {
        const data = await resp.json() as { content: string };
        setDraftContent(data.content);
      } else {
        toast({ title: "Could not trim post", variant: "destructive" });
      }
    } catch {
      toast({ title: "Could not trim post", variant: "destructive" });
    } finally {
      setIsTrimming(false);
    }
  };

  const handleShare = async (text: string, id: string = "main") => {
    if (navigator.share) {
      try { await navigator.share({ text }); } catch { /* dismissed by user */ }
    } else {
      handleCopy(text, id);
    }
  };

  const handleTrimAllPlatform = async (p: string) => {
    if (trimmingPlatform) return;
    setTrimmingPlatform(p);
    try {
      const text = editedAllResults[p] ?? allPlatformResults?.[p] ?? "";
      const resp = await fetch(`${baseUrl}/api/posts/trim`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-session-id": sessionId },
        body: JSON.stringify({ content: text, platform: p }),
      });
      if (resp.ok) {
        const data = await resp.json() as { content: string };
        setEditedAllResults((prev) => ({ ...prev, [p]: data.content }));
      } else {
        toast({ title: "Could not trim post", variant: "destructive" });
      }
    } catch {
      toast({ title: "Could not trim post", variant: "destructive" });
    } finally {
      setTrimmingPlatform(null);
    }
  };

  const handlePostFeedback = (rating: "up" | "down") => {
    if (!result || feedbackGiven) return;
    setFeedbackGiven(rating);
    feedbackMutation.mutate({ data: { postId: result.id, rating } });
  };

  const handlePostKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const isMac = navigator.platform.toUpperCase().includes("MAC");
    const mod = isMac ? e.metaKey : e.ctrlKey;
    if (mod && e.key === "z") {
      e.preventDefault();
      handleUndo();
    } else if (mod && e.key === "Enter") {
      e.preventDefault();
      const text = drafts ? (drafts[activeDraftIdx]?.content ?? "") : draftContent;
      if (text) handleCopy(text);
    }
  }, [handleUndo, drafts, activeDraftIdx, draftContent, handleCopy]);

  const handleUndoPush = useCallback((prev: string) => {
    setUndoStack(prevStack => [prev, ...prevStack].slice(0, 10));
  }, []);

  const isSingleLoading = isBatchLoading || refineMutation.isPending || autoGenerating;
  const isAllLoading = generateAllMutation.isPending;
  const isLoading = isSingleLoading || isAllLoading;

  const activeContent = drafts ? (drafts[activeDraftIdx]?.content ?? "") : draftContent;
  const charCount = activeContent.length;
  const wordCount = activeContent ? getWordCount(activeContent) : 0;
  const platformLimit = PLATFORM_CONFIG[platform as keyof typeof PLATFORM_CONFIG]?.charLimit ?? 0;

  return {
    currentIdea, setIdea, sessionId,
    showPaywall, dismissPaywall, showHardPaywall,
    mode, setMode,
    platform, setLocalPlatform,
    tone, setTone,
    copied, setCopied,
    savedToVault,
    allPlatformResults,
    generateError, generateAllError,
    postLengthOverride, setPostLengthOverride,
    feedbackGiven, setFeedbackGiven,
    feedbackMessage,
    advancedOpen, setAdvancedOpen, advancedCount,
    postStyle, setPostStyle,
    spacingStyle, setSpacingStyle,
    hookStyle, setHookStyle,
    perspective, setPerspective,
    humorLevel, setHumorLevel,
    audience, setAudience,
    emojiUsage, setEmojiUsage,
    targetWordCount, setTargetWordCount,
    customInstruction, setCustomInstruction,
    selectedVoiceState, setSelectedVoiceState,
    audienceDescription, setAudienceDescription,
    blendStyle, setBlendStyle,
    recentTopics, clearRecentContext, clearAdvanced,
    draftContent, setDraftContent,
    editedAllResults, setEditedAllResults,
    isTrimming, trimmingPlatform,
    voiceIntentBanner, setVoiceIntentBanner,
    drafts, activeDraftIdx, setActiveDraftIdx,
    undoStack,
    multiVariant, setMultiVariant,
    result,
    coachTip, coachLoading, coachDismissed, setCoachDismissed,
    voiceDnaOpen, setVoiceDnaOpen,
    needsVoiceTraining, voiceNudgeDismissed, setVoiceNudgeDismissed,
    voiceProfile, voiceSampleCount,
    hasPreferences, voiceStates,
    platformDesc, currentSuggestions, conflicts,
    topicVoiceInput, isIdeaVague,
    handleVoiceTranscriptForTopic,
    isLoading, isSingleLoading, isAllLoading,
    charCount, wordCount, platformLimit,
    handleGenerate, handleRefine, handleUndo,
    handleCopy, handleSaveToVault,
    handleTrimToLimit, handleShare, handleTrimAllPlatform,
    handlePostFeedback, handlePostKeyDown, handleUndoPush,
    updateActiveDraftContent, commitActiveDraft,
    feedbackMutationPending: feedbackMutation.isPending,
    createIdeaMutationPending: createIdeaMutation.isPending,
  };
}
